// Apollo
import { apolloClient } from '@/graphql';
import gql from 'graphql-tag';
import GetMotmData from '@/graphql/leagues/GetMotmData.gql';
// Round Robin Generator
import { generateRoundRobin } from '@/utils';

// Insert
import InsertLeagueTeams from '@/graphql/leagues_teams/insert/insertTeams.gql';

// Delete
import DeleteLeague from '@/graphql/leagues/delete/deleteLeague.gql';

export default {
	state: {
		leagues: [] as HasuraLeague[],
		allLeagues: [],
		totalLeagues: null as any,
	},

	// ================ Getters ================
	getters: {
		leagues(state: any) {
			return state.leagues;
		},
		allLeagues(state: any) {
			return state.allLeagues;
		},
		totalLeagues(state: any) {
			return state.totalLeagues;
		},
	},

	// ================ Mutations ================
	mutations: {
		setLeagues(state: any, leagues: any) {
			state.leagues = leagues;
		},
		setTotalLeaguesCount(state: any, totalCount: any) {
			state.totalLeagues = totalCount;
		},
		setallLeagues(state: any, allLeagues: any) {
			state.allLeagues = allLeagues;
		},
	},

	// ================= Actions =================
	// ================= Actions =================
	actions: {
		// TODO: Replace this with insertLeague
		async createNewLeague({ commit }: any, { features, matches }: any) {
			const league: any = {
				bbq_included: null,
				catchup_week: null,
				currency: null,
				day_of_week: features.dayOfTheWeek,
				default_game_length: features.defaultGameLength,
				// Reduce array of default pitches to a string of space seperated pitch IDs
				default_pitches: features.defaultFields
					.reduce((acc: string, cur: any) => {
						return acc + `${cur.id} `;
					}, '')
					.trim(),
				default_time_slot: features.defaultSlotDuration,
				default_number_of_halves: features.numberOfHalves,
				default_half_length: features.halfLength,
				default_kick_off_time: features.defaultKickOffTime,
				deposit: null,
				display_name: features.display_name,
				division: null,
				finals: true,
				free_agents: true,
				frequency: features.frequency,
				kick_off_date: features.firstGameDate,
				league_type: 'full',
				location: features.location,
				man_of_the_match: features.manOfTheMatch,
				number_of_rounds: null,
				player_type: features.playerType,
				regular_price: null,
				round_robin_format: features.roundRobinFormat,
				sales_tax: null,
				season: features.season,
				status: 'planned',
				student_price: null,
				team_size: features.teamSize,
			};

			const response = await apolloClient
				.mutate({
					mutation: gql`
						mutation insert_multiple_leagues($objects: [leagues_insert_input!]!) {
							insert_leagues(objects: $objects) {
								returning {
									id
								}
							}
						}
					`,
					// Parameters
					variables: {
						objects: [league],
					},
				})
				.catch(err => {
					let error;
					if (err.graphQLErrors) {
						if (err.graphQLErrors[0].extensions.code === 'constraint-violation') error = 'League name already exists';
					} else {
						error = 'League Creation Failed';
					}
					throw new Error(error);
				});

			return (response as any).data.insert_leagues.returning[0].id;
		},

		async insertLeague({ commit }: any, league: any) {
			console.time('insertLeague');

			const response = await apolloClient
				.mutate({
					mutation: gql`
						mutation insert_multiple_leagues($objects: [leagues_insert_input!]!) {
							insert_leagues(objects: $objects) {
								returning {
									id
								}
							}
						}
					`,
					variables: {
						objects: [league],
					},
				})
				.catch(err => {
					console.debug(err);
					throw new Error('League Creation Failed');
				});

			console.timeEnd('insertLeague');

			return (response as any).data.insert_leagues.returning[0].id;
		},

		async getLeagueById({ commit, state }: any, { leagueId }: any) {
			let _leagueId = leagueId;

			const response = await apolloClient.query({
				query: gql`
					query getLeague($_leagueId: uuid!) {
						leagues(where: { id: { _eq: $_leagueId } }) {
							id
							bbq_included
							catchup_week
							created_at
							currency
							day_of_week
							deposit
							display_name
							division
							default_pitches
							status
							published
							free_agents
							frequency
							default_game_length
							default_number_of_halves
							default_half_length
							default_kick_off_time
							kick_off_date
							location
							man_of_the_match
							number_of_rounds
							player_type
							regular_price
							round_robin_format
							sales_tax
							season
							student_price
							team_size
							default_time_slot
							league_type
							league_information_english
							league_information_dutch
							finals: join_finals_leagues {
								qualification_positions
								id
								league
								finals
								final {
									id
									display_name
								}
							}
							attachedMiniLeagues: join_leagues_mini_leagues {
								id
								qualification_positions
								mini_league
								league
								leagueByMiniLeague {
									id
									display_name
								}
							}
							parentLeagues: joinLeaguesMiniLeaguesByMiniLeague {
								league: leagueByLeague {
									id
									display_name
									leagueTeams: league_teams {
										league_id
										team_id
									}
								}
							}
							locationObj: locationByLocation {
								id
								name
								currency
								city {
									id
									name
									country {
										id
										name
									}
								}
							}
							league_teams {
								id
								points_carried
								played_carried
								won_carried
								drawn_carried
								lost_carried
								gs_carried
								gd_carried
								deleted_at
								team {
									id
									name
									placeholder
									type
								}
								placeholder_team {
									id
									name
									seeding_league
									seeding_match
									seeding_position
									seeding_group
									type
									league_teams {
										id
										is_active
										league_id
										placeholder_id
										team_id
									}
								}
							}
							linkedQualificationPositions: placeholder_teams {
								id
								name
								seeding_league
								seeding_match
								seeding_position
								seeding_group
								parent_league
								type
							}
							linkedQualificationSeedingPositions: placeholderTeamsBySeedingLeague {
								id
								name
								seeding_league
								seeding_match
								seeding_position
								seeding_group
								parent_league
								type
							}
							placeholdersInLeague: placeholderTeamsByParentLeague {
								name
								carry_points
								id
								parent_finals
								parent_group
								parent_league
								seeding_group
								seeding_league
								seeding_match
								seeding_position
								type
							}
							matches {
								id
								away_score
								away_team
								date
								game_length
								half_length
								home_score
								home_team
								is_friendly
								kick_off_time
								league
								location
								man_of_the_match
								name
								round
								number_of_halves
								placeholder_away_team
								placeholder_home_team
								postponed
								published
								slot_time
								tournament
								phHomeTeamObj: placeholderTeamByPlaceholderHomeTeam {
									id
									name
									seeding_league
									seeding_match
									seeding_position
									seeding_group
									type
								}
								phAwayTeamObj: placeholder_team {
									id
									name
									seeding_league
									seeding_match
									seeding_position
									seeding_group
									type
								}
								match_comments {
									id
									match
									text
									created_at
									private
									author: user {
										id
										first_name
										last_name
									}
								}
								pitch
								pitchObj: pitchByPitch {
									id
									name
									pitch_name
									location {
										id
										name
										city_id
									}
								}
								referee
								refereeObj: refereeByReferee {
									id
									first_name
									last_name
								}
								awayTeamObj: team {
									id
									name
									placeholder
									type
								}
								homeTeamObj: teamByHomeTeam {
									id
									name
									placeholder
									type
								}
								leagueObj: leagueByLeague {
									id
									display_name
								}
							}
						}
					}
				`,
				fetchPolicy: 'no-cache',
				variables: {
					_leagueId,
				},
			});

			const { leagues } = response.data;

			return leagues[0];
		},

		async getLinkedFinals({ commit, state }: any, { leagueId }: any) {
			let _leagueId = leagueId;

			const response = await apolloClient.query({
				query: gql`
					query getLeague($_leagueId: uuid!) {
						leagues(where: { id: { _eq: $_leagueId } }) {
							id
							finals: join_finals_leagues {
								final {
									id
									placeholder_teams {
										id
										seeding_league
										seeding_position
									}
									finals_groups {
										matches {
											id
											placeholder_home_team
											placeholder_away_team
											home_team
											away_team
										}
									}
									finals_knockouts {
										matches {
											id
											placeholder_home_team
											placeholder_away_team
											home_team
											away_team
										}
									}
								}
								qualification_positions
							}
						}
					}
				`,
				fetchPolicy: 'no-cache',
				variables: {
					_leagueId,
				},
			});

			const { leagues } = response.data;

			return leagues[0];
		},
		async getAllLeagues({ commit }: any) {
			const response = await apolloClient.query({
				query: gql`
					query getLeagues {
						leagues {
							id
							bbq_included
							catchup_week
							created_at
							currency
							day_of_week
							default_game_length
							default_half_length
							default_number_of_halves
							default_time_slot
							deposit
							display_name
							division
							finals
							free_agents
							frequency
							kick_off_date
							league_type
							location
							man_of_the_match
							number_of_rounds
							player_type
							published
							regular_price
							round_robin_format
							sales_tax
							season
							status
							student_price
							team_size
							locationObj: locationByLocation {
								id
								name
								city {
									id
									name
									country {
										id
										name
									}
								}
							}
							linkedQualificationPositions: placeholder_teams {
								id
								name
								seeding_league
								seeding_match
								seeding_position
								seeding_group
								type
							}
							linkedQualificationSeedingPositions: placeholderTeamsBySeedingLeague {
								id
								name
								seeding_league
								seeding_match
								seeding_position
								seeding_group
								type
							}
							join_leagues_mini_leagues {
								id
								qualification_positions
								mini_league
								league
							}
							placeholdersInLeague: placeholderTeamsByParentLeague {
								name
								carry_points
								id
								parent_finals
								parent_group
								parent_league
								seeding_group
								seeding_league
								seeding_match
								seeding_position
								type
							}
							league_teams {
								team {
									id
									name
									placeholder
									type
								}
							}
							matches {
								id
								home_score
								away_score
								date
								game_length
								kick_off_time
								location: locationByLocation {
									id
									name
									pitches {
										id
										name
									}
								}
								man_of_the_match
								match_comments {
									id
									match
									text
									created_at
									author: user {
										id
										first_name
										last_name
									}
								}
								pitch: pitchByPitch {
									id
									name
									pitch_name
									location_id
								}
								postponed
								referee: refereeByReferee {
									id
									first_name
									last_name
								}
								slot_time
								awayTeamObj: team {
									id
									name
									placeholder
									type
								}
								homeTeamObj: teamByHomeTeam {
									id
									name
									placeholder
									type
								}
							}
						}
					}
				`,
				fetchPolicy: 'no-cache',
			});
			await commit('setallLeagues', response.data.leagues);
			console.log('response', response);
		},

		async getLeagues({ commit }: any, payload: any = {}) {
			// Set default values if not provided
			const filters = payload.filters || {};
			const pagination = payload.pagination || {
				page: 1,
				itemsPerPage: 12,
			};

			const offset = (pagination.page - 1) * pagination.itemsPerPage;
			const limit = pagination.itemsPerPage;

			const now = new Date();
			const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
			const isoDate = oneDayAgo.toISOString();

			const statusConditions: any[] = [];
			if (filters.isPlanned) {
				statusConditions.push(
					{
						_and: [{ kick_off_date: { _gte: isoDate } }, { status: { _neq: 'finished' } }],
					},
					{
						_and: [{ published: { _eq: false } }, { status: { _neq: 'finished' } }],
					}
				);
			}

			if (filters.isActive) {
				statusConditions.push({
					_and: [{ published: { _eq: true } }, { kick_off_date: { _lt: isoDate } }, { status: { _neq: 'finished' } }],
				});
			}

			if (filters.isFinished) {
				statusConditions.push({
					_and: [{ status: { _eq: 'finished' } }, { published: { _eq: true } }],
				});
			}

			if (filters.isArchived) {
				statusConditions.push({
					_and: [{ status: { _eq: 'finished' } }, { published: { _eq: false } }],
				});
			}

			//search by text

			let orderBy: any = { kick_off_date: 'desc' };

			if (filters.sortBy === 'Date ASC') {
				orderBy = { kick_off_date: 'asc' };
			} else if (filters.sortBy === 'Date DESC') {
				orderBy = { kick_off_date: 'desc' };
			} else if (filters.sortBy === 'Created') {
				orderBy = { created_at: 'desc' };
			} else if (filters.sortBy == 'A-Z') {
				orderBy = {
					display_name: 'asc',
				};
			} else if (filters.sortBy == 'Z-A') {
				orderBy = {
					display_name: 'desc',
				};
			} else if (filters.sortBy == 'Results Needed') {
				orderBy = { kick_off_date: 'asc' };
			}

			const GET_LEAGUES = gql`
				query getLeagues($limit: Int!, $offset: Int!, $where: leagues_bool_exp, $order_by: [leagues_order_by!]) {
					leagues(where: $where, limit: $limit, offset: $offset, order_by: $order_by) {
						id
						bbq_included
						catchup_week
						created_at
						currency
						day_of_week
						default_game_length
						default_half_length
						default_number_of_halves
						default_time_slot
						deposit
						display_name
						division
						finals
						free_agents
						frequency
						kick_off_date
						league_type
						location
						man_of_the_match
						number_of_rounds
						player_type
						published
						regular_price
						round_robin_format
						sales_tax
						season
						status
						student_price
						team_size
						locationObj: locationByLocation {
							id
							name
							city {
								id
								name
								country {
									id
									name
								}
							}
						}
						linkedQualificationPositions: placeholder_teams {
							id
							name
							seeding_league
							seeding_match
							seeding_position
							seeding_group
							type
						}
						linkedQualificationSeedingPositions: placeholderTeamsBySeedingLeague {
							id
							name
							seeding_league
							seeding_match
							seeding_position
							seeding_group
							type
						}
						join_leagues_mini_leagues {
							id
							qualification_positions
							mini_league
							league
						}
						placeholdersInLeague: placeholderTeamsByParentLeague {
							name
							carry_points
							id
							parent_finals
							parent_group
							parent_league
							seeding_group
							seeding_league
							seeding_match
							seeding_position
							type
						}
						league_teams {
							team {
								id
								name
								placeholder
								type
							}
						}
						matches {
							id
							home_score
							away_score
							date
							game_length
							kick_off_time
							location: locationByLocation {
								id
								name
								pitches {
									id
									name
								}
							}
							man_of_the_match
							match_comments {
								id
								match
								text
								created_at
								author: user {
									id
									first_name
									last_name
								}
							}
							pitch: pitchByPitch {
								id
								name
								pitch_name
								location_id
							}
							postponed
							referee: refereeByReferee {
								id
								first_name
								last_name
							}
							slot_time
							awayTeamObj: team {
								id
								name
								placeholder
								type
							}
							homeTeamObj: teamByHomeTeam {
								id
								name
								placeholder
								type
							}
						}
					}
					leagues_aggregate(where: $where) {
						aggregate {
							count
						}
					}
				}
			`;

			const where: any = statusConditions.length > 0 ? { _or: statusConditions } : {};

			// Add display_name search filter

			if (filters.searchField && filters.searchField.trim() !== '') {
				const searchFilter = {
					display_name: { _ilike: `%${filters.searchField.trim()}%` },
				};

				// Combine with existing filter using _and
				if (Object.keys(where).length > 0) {
					where._and = where._and || [];
					where._and.push(searchFilter);
				} else {
					Object.assign(where, searchFilter);
				}
			}

			//filter by country id

			if (filters.country && filters.country.trim() !== '') {
				const countryFilter = {
					locationByLocation: {
						city: {
							country: {
								id: { _eq: filters.country },
							},
						},
					},
				};

				if (Object.keys(where).length > 0) {
					where._and = where._and || [];
					where._and.push(countryFilter);
				} else {
					Object.assign(where, countryFilter);
				}
			}

			// filter by city
			if (Array.isArray(filters.cities) && filters.cities.length > 0) {
				const cityFilter = {
					locationByLocation: {
						city: {
							id: { _in: filters.cities },
						},
					},
				};

				if (Object.keys(where).length > 0) {
					where._and = where._and || [];
					where._and.push(cityFilter);
				} else {
					Object.assign(where, cityFilter);
				}
			}

			//filter by locations//venue
			if (Array.isArray(filters.locations) && filters.locations.length > 0) {
				const locationFilter = {
					location: { _in: filters.locations },
				};

				if (Object.keys(where).length > 0) {
					where._and = where._and || [];
					where._and.push(locationFilter);
				} else {
					Object.assign(where, locationFilter);
				}
			}

			//filter by player type
			if (filters.playerType && filters.playerType !== 'all' && filters.playerType.trim() !== '') {
				const playerTypeFilter = {
					player_type: { _eq: filters.playerType },
				};

				if (Object.keys(where).length > 0) {
					where._and = where._and || [];
					where._and.push(playerTypeFilter);
				} else {
					Object.assign(where, playerTypeFilter);
				}
			}

			if (filters.sortBy == 'Results Needed') {
				const isoDate = new Date(Date.now()).toISOString().split('T')[0];

				const strickCondition = {
					_and: [
						{ published: { _eq: true } },
						{ status: { _neq: 'finished' } },
						{
							matches: {
								// This means "any match that satisfies the following"
								_and: [{ home_score: { _is_null: true } }, { away_score: { _is_null: true } }, { date: { _lt: isoDate } }],
							},
						},
					],
				};

				if (Object.keys(where).length > 0) {
					where._and = where._and || [];
					where._and.push(strickCondition);
				} else {
					Object.assign(where, strickCondition);
				}
			}

			let response;
			try {
				response = await apolloClient.query({
					query: GET_LEAGUES,
					variables: {
						limit,
						offset,
						where,
						order_by: orderBy,
					},
					fetchPolicy: 'no-cache',
				});
			} catch (err) {
				console.error('Error fetching leagues:', err);
				return {
					leagues: [],
					total: 0,
					error: err,
				};
			}

			const leagues = response.data.leagues || [];
			const totalCount = response.data.leagues_aggregate.aggregate.count || 0;

			await commit('setLeagues', leagues);
			await commit('setTotalLeaguesCount', totalCount);

			return {
				leagues,
				total: totalCount,
			};
		},
		async generateRoundRobin({ commit }: any, { teams, placeholders, playedMatches, roundRobinFormat }: any) {
			const allRounds = generateRoundRobin(teams, placeholders, playedMatches, roundRobinFormat);
			return allRounds;
		},

		async deleteLeague({ state, commit }: any, { leagueId }: any) {
			// Delete league
			const response: any = await apolloClient
				.mutate({
					mutation: DeleteLeague,
					// Parameters
					variables: {
						leagueId,
					},
				})
				.catch(err => {
					console.debug(err);
				});

			return response.data.delete_leagues.returning[0];
		},

		async insertLeagueMiniLeagueJoin({ state, commit }: any, joins: any) {
			console.log('insertLeagueMiniLeagueJoin');

			const response: any = await apolloClient
				.mutate({
					mutation: gql`
						mutation InsertLeagueMiniLeagueJoin($objects: [join_leagues_mini_leagues_insert_input!]!) {
							insert_join_leagues_mini_leagues(objects: $objects) {
								affected_rows
							}
						}
					`,
					// Parameters
					variables: {
						objects: joins,
					},
				})
				.catch(err => {
					console.error('insertLeagueMiniLeagueJoin');
					console.debug(err);
				});

			return response.data;
		},

		async insertLeagueTeamRegistrations({ state, commit }: any, registrations: any) {
			// console.log('insertLeagueTeamRegistrations');

			const response: any = await apolloClient
				.mutate({
					mutation: gql`
						mutation insertLeagueTeamRegistration($objects: [join_league_teams_insert_input!]!) {
							insert_join_league_teams(objects: $objects) {
								affected_rows
							}
						}
					`,
					// Parameters
					variables: {
						objects: registrations,
					},
				})
				.catch(err => {
					console.debug(err);
				});

			return response.data;
		},

		async registerTeamsInLeague({ state, commit }: any, { leagueId, teams }: any) {
			// console.log('registerTeamsInLeague');
			try {
				const objects = teams.map((teamId: string) => {
					return {
						league_id: leagueId,
						team_id: teamId,
					};
				});

				const response: any = await apolloClient
					.mutate({
						mutation: InsertLeagueTeams,
						// Parameters
						variables: {
							objects,
						},
					})
					.catch(err => {
						console.debug(err);
					});

				return response.data.insert_join_league_teams.returning;
			} catch (error) {
				return null;
			}
		},

		async registerTeamsInLeagueV2({ state, commit }: any, { league, teams }: any) {
			console.log('registerTeamsInLeague...');

			const objects = teams.map((team: any) => {
				const pointsCarried = team.points_carried;
				const playedCarried = team.played_carried;
				const wonCarried = team.won_carried;
				const drawnCarried = team.drawn_carried;
				const lostCarried = team.lost_carried;
				const gsCarried = team.gs_carried;
				const gdCarried = team.gd_carried;
				return {
					league_id: league.id,
					team_id: team.id,
					points_carried: pointsCarried ? pointsCarried : 0,
					played_carried: playedCarried ? playedCarried : 0,
					won_carried: wonCarried ? wonCarried : 0,
					lost_carried: lostCarried ? lostCarried : 0,
					drawn_carried: drawnCarried ? drawnCarried : 0,
					gs_carried: gsCarried ? gsCarried : 0,
					gd_carried: gdCarried ? gdCarried : 0,
				};
			});

			const response: any = await apolloClient
				.mutate({
					mutation: InsertLeagueTeams,
					variables: {
						objects,
					},
				})
				.catch(err => {
					console.error('registerTeamsInLeagueV2');
					console.debug(err);
				});

			return response.data.insert_join_league_teams.returning;
		},

		async updateLeagueRegistry({ state, commit }: any, registerEntry: any) {
			// console.log('updateLeagueRegistry');

			const response: any = await apolloClient
				.mutate({
					mutation: gql`
						mutation updateLeagueTeams($id: uuid!, $object: join_league_teams_set_input) {
							update_join_league_teams(where: { id: { _eq: $id } }, _set: $object) {
								returning {
									id
								}
							}
						}
					`,
					// Parameters
					variables: {
						id: registerEntry.id,
						object: registerEntry,
					},
				})
				.catch(err => {
					console.debug(err);
				});

			return response.data;
		},

		async deleteLeagueRegistry({ state, commit }: any, registerEntry: any) {
			// console.log('deleteLeagueRegistry');

			const response: any = await apolloClient
				.mutate({
					mutation: gql`
						mutation deleteLeagueRegistry($id: uuid!) {
							delete_league_teams(where: { id: { _eq: $id } }) {
								affected_rows
							}
						}
					`,
					// Parameters
					variables: {
						id: registerEntry.id,
					},
				})
				.catch(err => {
					console.debug(err);
				});

			return response.data;
		},

		async registerPlaceholdersInLeague({ state, commit }: any, { leagueId, placeholders }: any) {
			// console.log('registerPlaceholdersInLeague');

			const objects = placeholders.map(({ id }: HasuraPlaceholderTeam) => {
				return {
					league_id: leagueId,
					placeholder_id: id,
				};
			});

			const response: any = await apolloClient
				.mutate({
					mutation: InsertLeagueTeams,
					// Parameters
					variables: {
						objects,
					},
				})
				.catch(err => {
					console.debug(err);
				});

			return response.data.insert_join_league_teams.returning;
		},

		async updateLeague({ commit, state }: any, league: HasuraLeague) {
			console.log('updating league...');
			const response = await apolloClient.query({
				query: gql`
					mutation update_leagues($id: uuid!, $object: leagues_set_input!) {
						update_leagues(where: { id: { _eq: $id } }, _set: $object) {
							returning {
								id
							}
						}
					}
				`,
				fetchPolicy: 'no-cache',
				variables: {
					id: league.id,
					object: league,
				},
			});

			const { update_leagues } = response.data;
			console.log('update_leagues', update_leagues);
			return update_leagues[0];
		},

		async getLeaguesFinals({ commit, state }: any, { id }: any) {
			console.time('getLeaguesFinals');

			const response = await apolloClient.query({
				query: gql`
					query getLeaguesFinals($id: uuid!) {
						join_finals_leagues(where: { league: { _eq: $id } }) {
							final {
								id
								display_name
								day_of_the_week
								created_at
								color_name
								color
								kick_off_date
								location
								number_of_teams
								player_type
								status
								team_size
								group_stage
							}
						}
					}
				`,
				fetchPolicy: 'no-cache',
				variables: {
					id,
				},
			});

			console.timeEnd('getLeaguesFinals');

			return response.data.join_finals_leagues;
		},
		async getMotmData({ commit }: any, ids: []) {
			try {
				const promises = ids.map((id: any) => {
					return apolloClient.query({
						query: GetMotmData,
						variables: {
							id,
						},
					});
				});
				const responses = await Promise.all(promises);
				const result: any[] = [];
				responses.map((res: any) => {
					res.data.motmForLeague &&
						res.data.motmForLeague.matches &&
						res.data.motmForLeague.matches.length &&
						res.data.motmForLeague.matches.map((match: any) => result.push(match));
				});
				return result;
			} catch (error) {
				console.log('error is ', error);
				return [];
			}
		},
	},
};
